import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  Users, Building2, ChevronDown, ChevronRight, UserCircle, 
  Mail, Phone, Briefcase, ZoomIn, ZoomOut
} from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";

function EmployeeCard({ emp }) {
  return (
    <div className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-lg">
          {emp.full_name?.[0] || "?"}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{emp.full_name}</p>
          <p className="text-sm text-muted-foreground truncate">{emp.job_title || "No title"}</p>
          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
            <Mail className="h-3 w-3" />
            <span className="truncate">{emp.email}</span>
          </div>
          {emp.phone && (
            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
              <Phone className="h-3 w-3" />
              <span>{emp.phone}</span>
            </div>
          )}
        </div>
      </div>
      {emp.manager?.full_name && (
        <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
          <span>Reports to: </span>
          <span className="font-medium text-foreground">{emp.manager.full_name}</span>
        </div>
      )}
    </div>
  );
}

// Helper function to flatten hierarchy with indentation level
function flattenTree(nodes, level = 0) {
  const result = [];
  for (const node of nodes) {
    result.push({ ...node, level });
    if (node.children && node.children.length > 0) {
      result.push(...flattenTree(node.children, level + 1));
    }
  }
  return result;
}

export default function OrganizationChart() {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("hierarchy"); // hierarchy, department, list
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedDept, setSelectedDept] = useState("all");
  const [zoom, setZoom] = useState(100);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const [empRes, deptRes] = await Promise.all([
        axios.get(`${API}/hr/employees`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hr/departments`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setEmployees(empRes.data);
      setDepartments(deptRes.data);
      // Expand all top-level nodes by default
      const topLevel = empRes.data.filter(e => !e.manager_id).map(e => e.hr_employee_id);
      setExpandedNodes(new Set(topLevel));
    } catch (error) {
      console.error("Error fetching org chart data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Build hierarchy tree
  const buildTree = useMemo(() => {
    const employeeMap = new Map();
    employees.forEach(emp => employeeMap.set(emp.hr_employee_id, { ...emp, children: [] }));
    
    const roots = [];
    employees.forEach(emp => {
      if (emp.manager_id && employeeMap.has(emp.manager_id)) {
        employeeMap.get(emp.manager_id).children.push(employeeMap.get(emp.hr_employee_id));
      } else {
        roots.push(employeeMap.get(emp.hr_employee_id));
      }
    });
    
    return roots;
  }, [employees]);

  // Filter by department
  const filteredTree = useMemo(() => {
    if (selectedDept === "all") return buildTree;
    
    const filterNode = (node) => {
      if (node.department_id === selectedDept) return { ...node };
      const filteredChildren = node.children.map(filterNode).filter(Boolean);
      if (filteredChildren.length > 0) {
        return { ...node, children: filteredChildren };
      }
      return null;
    };
    
    return buildTree.map(filterNode).filter(Boolean);
  }, [buildTree, selectedDept]);

  // Group by department
  const byDepartment = useMemo(() => {
    const grouped = {};
    departments.forEach(dept => {
      grouped[dept.department_id] = {
        department: dept,
        employees: employees.filter(e => e.department_id === dept.department_id)
      };
    });
    // Add unassigned
    const unassigned = employees.filter(e => !e.department_id);
    if (unassigned.length > 0) {
      grouped["unassigned"] = {
        department: { name: "Unassigned", department_id: "unassigned" },
        employees: unassigned
      };
    }
    return grouped;
  }, [employees, departments]);

  const toggleNode = useCallback((nodeId) => {
    setExpandedNodes(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(nodeId)) {
        newExpanded.delete(nodeId);
      } else {
        newExpanded.add(nodeId);
      }
      return newExpanded;
    });
  }, []);

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Young Serif, serif' }}>Organization Chart</h1>
            <p className="text-muted-foreground">View team structure and reporting relationships</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={selectedDept} onValueChange={setSelectedDept}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept.department_id} value={dept.department_id}>{dept.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-1 border rounded-lg p-1">
              <Button 
                variant={viewMode === "hierarchy" ? "secondary" : "ghost"} 
                size="sm"
                onClick={() => setViewMode("hierarchy")}
              >
                <Users className="h-4 w-4" />
              </Button>
              <Button 
                variant={viewMode === "department" ? "secondary" : "ghost"} 
                size="sm"
                onClick={() => setViewMode("department")}
              >
                <Building2 className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex items-center gap-1 border rounded-lg p-1">
              <Button variant="ghost" size="sm" onClick={() => setZoom(Math.max(50, zoom - 10))}>
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-xs w-12 text-center">{zoom}%</span>
              <Button variant="ghost" size="sm" onClick={() => setZoom(Math.min(150, zoom + 10))}>
                <ZoomIn className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left' }}>
          {viewMode === "hierarchy" ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Reporting Structure
                </CardTitle>
                <CardDescription>Click on employees with teams to expand/collapse</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {filteredTree.length > 0 ? (
                  flattenTree(filteredTree).map(node => {
                    const hasChildren = node.children && node.children.length > 0;
                    const isExpanded = expandedNodes.has(node.hr_employee_id);
                    // Skip children that aren't expanded
                    if (node.level > 0) {
                      // Find parent and check if expanded
                      const findParent = (nodes, childId, depth = 0) => {
                        for (const n of nodes) {
                          if (n.children?.some(c => c.hr_employee_id === childId)) {
                            return expandedNodes.has(n.hr_employee_id);
                          }
                          if (n.children) {
                            const found = findParent(n.children, childId, depth + 1);
                            if (found !== undefined) return found;
                          }
                        }
                        return undefined;
                      };
                      // For simplicity, check if any ancestor is collapsed
                      let checkNode = node;
                      let parentExpanded = true;
                      employees.forEach(emp => {
                        if (emp.hr_employee_id === node.manager_id) {
                          if (!expandedNodes.has(emp.hr_employee_id)) {
                            parentExpanded = false;
                          }
                        }
                      });
                      if (!parentExpanded) return null;
                    }
                    
                    return (
                      <div key={node.hr_employee_id} style={{ marginLeft: node.level * 24 }}>
                        <div 
                          className={`flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 cursor-pointer transition-colors ${node.level === 0 ? 'border-primary/30' : ''}`}
                          onClick={() => hasChildren && toggleNode(node.hr_employee_id)}
                        >
                          {hasChildren ? (
                            <button className="p-1 hover:bg-muted rounded" type="button">
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                          ) : (
                            <div className="w-6" />
                          )}
                          
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                            {node.full_name?.[0] || "?"}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{node.full_name}</p>
                            <p className="text-xs text-muted-foreground truncate">{node.job_title || "No title"}</p>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {node.department && (
                              <Badge variant="outline" className="text-xs">{node.department}</Badge>
                            )}
                            {hasChildren && (
                              <Badge variant="secondary" className="text-xs">{node.children.length}</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <UserCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No employees found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {Object.values(byDepartment).map(({ department, employees: deptEmployees }) => (
                <Card key={department.department_id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-primary" />
                        {department.name}
                      </CardTitle>
                      <Badge variant="secondary">{deptEmployees.length}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {deptEmployees.map(emp => (
                      <EmployeeCard key={emp.hr_employee_id} emp={emp} />
                    ))}
                    {deptEmployees.length === 0 && (
                      <p className="text-center text-muted-foreground py-4">No employees</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{employees.length}</p>
                  <p className="text-sm text-muted-foreground">Total Employees</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Building2 className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{departments.length}</p>
                  <p className="text-sm text-muted-foreground">Departments</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <Briefcase className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{employees.filter(e => !e.manager_id).length}</p>
                  <p className="text-sm text-muted-foreground">Top Level</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/10">
                  <UserCircle className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{employees.filter(e => e.children?.length > 0 || employees.some(x => x.manager_id === e.hr_employee_id)).length}</p>
                  <p className="text-sm text-muted-foreground">Managers</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AgentLayout>
  );
}
